/**
 * Dados da Home — o que o produto realmente sabe sobre ESTA viagem.
 *
 * A versão anterior deste arquivo descrevia a Home do protótipo e era
 * preenchida pelo dataset de exemplo: clima, fotos do dia, pessoas no mapa,
 * itens de compras. Numa viagem recém-criada nada disso existe — e mostrar
 * mesmo assim é inventar conteúdo.
 *
 * Agora cada campo tem uma origem real declarada, e **a ausência é
 * representável**: `null` quer dizer "não há", e a tela responde com estado
 * vazio e um caminho para preencher, nunca com um número plausível.
 *
 * Módulos que ainda não existem (Compras, Memórias) não aparecem aqui. Um
 * atalho com contagem falsa é pior do que atalho nenhum.
 */

/** O que está acontecendo agora — ou o que vem a seguir — no dia de hoje. */
export interface AtividadeDeAgora {
  id: string;
  /** `HH:MM`, 24h, como está na atividade. */
  time: string;
  title: string;
  location: string | null;
  /** `true` quando já começou; `false` quando ainda vai começar. */
  emCurso: boolean;
  /** Miniatura da capa, quando a atividade tem uma. */
  coverThumbUrl?: string | null;
}

export interface AtividadeDoDiaResumo {
  id: string;
  title: string;
  startTime: string;
  locationName: string | null;
  status: string;
}

export interface HomeData {
  /**
   * Preparação, derivada dos dias reais da viagem.
   * `itineraryPercent` = dias com ao menos uma atividade ÷ total de dias.
   */
  preparation: {
    itineraryPercent: number;
    daysPlanned: number;
    totalDays: number;
    /** Participantes ativos, do endpoint de membros. */
    confirmedPeople: number;
  };
  /** `null` = hoje não tem nada planejado, ou hoje está fora da viagem. */
  now: AtividadeDeAgora | null;
  /** Todas as atividades vivas de hoje, na ordem do dia (Home v2: chips). */
  atividadesDeHoje: AtividadeDoDiaResumo[];
  /** Antes da viagem: hora da 1ª atividade do 1º dia ("22:15"), ou `null`. */
  horaDaPartida: string | null;
  /** "1 de 3 atividades concluídas"; `null` sem atividade viva hoje. */
  progresso: { concluidas: number; total: number } | null;
  /** Minutos até `now` começar; `null` se já começou ou não há próxima. */
  minutosAteProxima: number | null;
  /** `null` enquanto o resumo financeiro não respondeu. */
  finance: {
    myTotalMinor: number;
    balanceMinor: number;
    groupTotalMinor: number;
  } | null;
}

/** Viagem recém-criada: os dias existem, o conteúdo não. */
export const emptyHomeData: HomeData = {
  preparation: {
    itineraryPercent: 0,
    daysPlanned: 0,
    totalDays: 0,
    confirmedPeople: 0,
  },
  now: null,
  atividadesDeHoje: [],
  horaDaPartida: null,
  progresso: null,
  minutosAteProxima: null,
  finance: null,
};
